import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  FormControl,
  FormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { NgToastService } from 'ng-angular-popup';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { IttWrapperComponent } from '../itt-wrapper/itt-wrapper.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { RfpLabelService } from '../../../rfp-labels.service';

@Component({
  selector: 'app-itt-work-packages',
  standalone: true,
  imports: [
    MatChipsModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  templateUrl: './itt-work-packages.component.html',
  styleUrl: './itt-work-packages.component.css',
})
export class IttWorkPackagesComponent implements OnInit {
  @Output() workPackagesDataEmitter: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() ITTDeveloperData: any;
  @Input() isEdit: boolean;

  // Will have all work packages of the project
  projectWorkPackages: any;
  selectAllWP: boolean = false;

  projectId: number;
  projectName: string;
  ittId: number = 0;

  titleControl = new FormControl('', Validators.required);
  selectedWorkPackagesControl = new FormControl(null);
  additionalInfoControl = new FormControl();
  labels: any;
  isRFPorRFQ: string;

  constructor(
    private toast: NgToastService,
    private tenderingService: TenderingService,
    private ittsections: IttWrapperComponent,
    private route: ActivatedRoute,
    private projectService: ProjectsService,
    private rfpLabelService: RfpLabelService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.url.subscribe(() => {
      this.updateRFPorRFQFlag();
      const currentRoute = this.router.url; // Get full URL path
      this.rfpLabelService.updateLabelsFromRoute(currentRoute);
    });
    this.rfpLabelService.labels$.subscribe((labels) => {
      this.labels = labels;
    });
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * will work on both create and edit as we have subcribed to it
     * and whenever the values changes, new value will be emitted
     */
    this.tenderingService.ittId$.subscribe((ittId) => {
      this.ittId = ittId;
    });

    this.getProjectWorkPackages();

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (this.ITTDeveloperData) {
      this.titleControl.setValue(this.ITTDeveloperData?.title);
      this.additionalInfoControl.setValue(
        this.ITTDeveloperData?.work_packages_des
      );

      const savedWorkPackages =
        this.ITTDeveloperData?.p_w_p.map((workpackage: any) => ({
          id: workpackage?.p_w_p_id,
          name: workpackage?.name,
        })) || [];
      this.selectedWorkPackagesControl.setValue(savedWorkPackages);
    }
  }

  updateRFPorRFQFlag() {
    const currentRoute = this.router.url.toLowerCase(); // Get full URL path
    if(currentRoute.includes('rfp')){
      this.isRFPorRFQ = 'RFP';
    }else if(currentRoute.includes('rfq')){
      this.isRFPorRFQ = 'RFQ'
    }
    console.log('isRFPorRFQ:', this.isRFPorRFQ);
  }

  // Get project selected work packages
  getProjectWorkPackages() {
    this.projectService
      .getProjectSelectedWorkPackages(this.projectId)
      .subscribe({
        next: (response: any) => {
          if (response?.meta?.code == 200) {
            this.projectWorkPackages = response?.data;

            // If all work packages are selected, then make checkbox = true
            const selectedWorkPackagesLength =
              this.selectedWorkPackagesControl.value?.length || 0;
            const projectWorkPackagesLength =
              this.projectWorkPackages?.length || 0;
            if (selectedWorkPackagesLength === projectWorkPackagesLength) {
              this.selectAllWP = true;
            }

            return;
          }
          console.error('Failed to fetch ITT initiation data:');
        },
        error: (error: any) => {
          console.error('Failed to fetch ITT initiation data:', error);
        },
      });
  }

  // When we select/un-select a work package, add/remove it from selectedWorkPackagesControl
  toggleWorkPackageSelection(event: Event, workpackage: any): void {
    const checkbox = event.target as HTMLInputElement;
    const currentValue = this.selectedWorkPackagesControl.value || []; // Get current value as an array

    if (checkbox.checked) {
      // Add the selected work package to the array
      this.selectedWorkPackagesControl.setValue([
        ...currentValue,
        {
          id: workpackage?.id,
          name: workpackage?.work_package?.name,
        },
      ]);
    } else {
      // Remove the work package from the array
      const updatedValue = currentValue.filter(
        (wp: any) => wp?.id !== workpackage?.id
      );
      this.selectedWorkPackagesControl.setValue(updatedValue);
      this.selectAllWP = false;
    }
    this.selectedWorkPackagesControl.updateValueAndValidity(); // Ensure the control reflects the updated value
  }

  // To show check box checked or not based on the id of work package
  isSelected(id: number): boolean {
    const selectedPackages = this.selectedWorkPackagesControl.value || []; // Get the value of the FormControl
    return selectedPackages.some((wp: any) => wp?.id === id);
  }

  selectAllWorkPackages(): void {
    this.selectAllWP = !this.selectAllWP; // Toggle the select-all state

    if (this.selectAllWP) {
      // Select all work packages
      const allWorkPackages =
        this.projectWorkPackages.map((workpackage: any) => ({
          id: workpackage?.id,
          name: workpackage?.work_package?.name,
        })) || [];
      this.selectedWorkPackagesControl.setValue(allWorkPackages);
    } else {
      // Deselect all work packages
      this.selectedWorkPackagesControl.setValue([]);
    }
  }

  prepareDataToSubmit() {
    const pwpData = this.selectedWorkPackagesControl.value;
    const data = {
      ...((this.isEdit || this.ittId) && { id: this.ittId }),
      title: this.titleControl.value,
      work_packages_des: this.additionalInfoControl.value,
      p_w_p: pwpData,
      type: this.isRFPorRFQ,
    };

    return data;
  }

  moveToNextStep() {
    this.titleControl.markAsTouched();
    if (!this.titleControl.value) {
      this.toast.warning('Please fill the required fields', 'WARNING', 4000);
      return;
    }

    const dataToSubmit = this.prepareDataToSubmit();

    this.tenderingService
      .storeDeveloperITTWorkPackagesData(dataToSubmit, this.projectId)
      .subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.toast.success('Work Packages Submitted submitted', 'SUCCESS');
            this.workPackagesDataEmitter.emit(response?.data);
            this.ittsections.nextStep();
            return;
          }
          this.toast.danger(
            'Work packages details could not be saved',
            'DANGER',
            4000
          );
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
  }

  moveToPreviousStep() {
    this.ittsections.previousStep();
  }
}
